import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

export default function ContactSection() {
  const { t } = useTranslation();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError(false);
    const form = e.currentTarget;
    const formData = new FormData(form);

    const data: Record<string, string> = {};
    formData.forEach((value, key) => {
      if (key !== '_honey') {
        data[key] = value.toString();
      }
    });

    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/send-contact-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          fullname: data.fullname,
          company: data.company,
          email: data.email,
          phone: data.phone || '',
          service_type: data.service_type || '',
          message: data.message,
        }),
      });

      const result = await res.json();

      if (!res.ok) {
        throw new Error(result.error || 'Bir hata olustu');
      }

      setSubmitted(true);
    } catch (err) {
      console.error('Form submit error:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    { icon: 'ri-map-pin-2-line', label: t('contact.addressLabel'), value: t('contact.addressValue') },
    { icon: 'ri-phone-line', label: t('contact.phoneLabel'), value: '+90 212 220 52 56' },
    { icon: 'ri-mail-line', label: t('contact.emailLabel'), value: 'info@okayisu.com' },
    { icon: 'ri-time-line', label: t('contact.hoursLabel'), value: t('contact.hoursValue') },
  ];

  return (
    <section className="py-24 px-6 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-full px-4 py-1.5 mb-5">
            <span className="w-1 h-1 bg-amber-500 rounded-full" />
            <span className="text-amber-600 text-xs tracking-[0.2em] uppercase font-semibold" style={{ fontFamily: "'Montserrat', sans-serif" }}>{t('contactPage.info.badge')}</span>
          </div>
          <h2 className="text-gray-900 text-2xl md:text-3xl font-bold mb-3" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            {t('contactPage.info.title')}{' '}
            <span className="text-amber-500">{t('contactPage.info.accent')}</span>
          </h2>
          <p className="text-gray-500 text-sm max-w-lg mx-auto" style={{ fontFamily: "'Inter', sans-serif" }}>{t('contactPage.info.desc')}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Left: Contact Info + Map */}
          <div>
            <div className="space-y-5 mb-8">
              {contactInfo.map((item, idx) => (
                <div key={idx} className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-amber-50 border border-amber-100 rounded-xl flex-shrink-0">
                    <i className={`${item.icon} text-amber-500 text-base`} />
                  </div>
                  <div>
                    <div className="text-gray-400 text-xs mb-0.5 font-medium" style={{ fontFamily: "'Montserrat', sans-serif" }}>{item.label}</div>
                    <div className="text-gray-700 text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>{item.value}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="rounded-2xl overflow-hidden border border-gray-100 h-48">
              <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d632.1314825664213!2d28.96910381451634!3d41.060732211142174!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14cab73b6818adab%3A0x2fc921611133962e!2sOkayisu%20LED%20Ayd%C4%B1nlatma%20Sistemleri!5e1!3m2!1str!2str!4v1781003597393!5m2!1str!2str" width="100%" height="192" style={{ border: 0 }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="OKAYISU Location" />
            </div>
          </div>

          {/* Right: Form */}
          <div className="bg-gray-50 border border-gray-100 rounded-3xl p-8" style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
            {submitted ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-12">
                <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mb-5">
                  <i className="ri-check-line text-green-600 text-2xl" />
                </div>
                <h3 className="text-gray-900 text-xl font-bold mb-3" style={{ fontFamily: "'Montserrat', sans-serif" }}>{t('contact.successTitle')}</h3>
                <p className="text-gray-500 text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>{t('contact.successMsg')}</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }} aria-hidden="true">
                  <input type="text" name="_honey" tabIndex={-1} autoComplete="off" />
                </div>
                <h3 className="text-gray-900 text-lg font-bold mb-5" style={{ fontFamily: "'Montserrat', sans-serif" }}>{t('contact.formTitle')}</h3>
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center gap-2">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0"><i className="ri-error-warning-line text-red-500 text-sm" /></div>
                    <p className="text-red-700 text-xs" style={{ fontFamily: "'Inter', sans-serif" }}>Mesajınız gönderilirken bir hata oluştu. Lütfen tekrar deneyin.</p>
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-600 text-xs mb-1.5 tracking-wide font-medium" style={{ fontFamily: "'Montserrat', sans-serif" }}>{t('contact.fullName')}</label>
                    <input type="text" name="fullname" required placeholder={t('contact.fullNamePlaceholder')} className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-gray-800 text-sm placeholder-gray-300 focus:outline-none focus:border-amber-400 transition-colors duration-200" style={{ fontFamily: "'Inter', sans-serif" }} />
                  </div>
                  <div>
                    <label className="block text-gray-600 text-xs mb-1.5 tracking-wide font-medium" style={{ fontFamily: "'Montserrat', sans-serif" }}>{t('contact.company')}</label>
                    <input type="text" name="company" required placeholder={t('contact.companyPlaceholder')} className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-gray-800 text-sm placeholder-gray-300 focus:outline-none focus:border-amber-400 transition-colors duration-200" style={{ fontFamily: "'Inter', sans-serif" }} />
                  </div>
                </div>
                <div>
                  <label className="block text-gray-600 text-xs mb-1.5 tracking-wide font-medium" style={{ fontFamily: "'Montserrat', sans-serif" }}>{t('contact.email')}</label>
                  <input type="email" name="email" required placeholder={t('contact.emailPlaceholder')} className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-gray-800 text-sm placeholder-gray-300 focus:outline-none focus:border-amber-400 transition-colors duration-200" style={{ fontFamily: "'Inter', sans-serif" }} />
                </div>
                <div>
                  <label className="block text-gray-600 text-xs mb-1.5 tracking-wide font-medium" style={{ fontFamily: "'Montserrat', sans-serif" }}>{t('contact.phone')}</label>
                  <input type="tel" name="phone" placeholder={t('contact.phonePlaceholder')} className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-gray-800 text-sm placeholder-gray-300 focus:outline-none focus:border-amber-400 transition-colors duration-200" style={{ fontFamily: "'Inter', sans-serif" }} />
                </div>
                <div>
                  <label className="block text-gray-600 text-xs mb-1.5 tracking-wide font-medium" style={{ fontFamily: "'Montserrat', sans-serif" }}>{t('contact.serviceType')}</label>
                  <select name="service_type" className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-gray-800 text-sm focus:outline-none focus:border-amber-400 transition-colors duration-200" style={{ fontFamily: "'Inter', sans-serif" }}>
                    <option value="">{t('contact.serviceTypePlaceholder')}</option>
                    <option value="Mimari Aydınlatma">{t('contact.serviceOption1')}</option>
                    <option value="Endüstriyel Aydınlatma">{t('contact.serviceOption2')}</option>
                    <option value="Dış Cephe Aydınlatma">{t('contact.serviceOption3')}</option>
                    <option value="İç Mekan Aydınlatma">{t('contact.serviceOption4')}</option>
                    <option value="Enerji Dönüşüm Projesi">{t('contact.serviceOption5')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-gray-600 text-xs mb-1.5 tracking-wide font-medium" style={{ fontFamily: "'Montserrat', sans-serif" }}>{t('contact.message')}</label>
                  <textarea name="message" required maxLength={500} rows={4} placeholder={t('contact.messagePlaceholder')} className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-gray-800 text-sm placeholder-gray-300 focus:outline-none focus:border-amber-400 transition-colors duration-200 resize-none" style={{ fontFamily: "'Inter', sans-serif" }} />
                </div>
                <button type="submit" disabled={loading} className="w-full bg-amber-500 hover:bg-amber-600 disabled:bg-amber-300 text-white font-bold py-3.5 rounded-xl text-sm tracking-wide transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  {loading ? <><i className="ri-loader-4-line animate-spin" />{t('contact.sending')}</> : <>{t('contact.submitBtn')}<i className="ri-arrow-right-line" /></>}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}